'use client'

import { useState, useEffect } from 'react'
import { GlobalSidebar } from '@/components/ui/global-sidebar'
import { useTranslations } from '@/lib/i18n'
import {
  ChevronDown,
  ChevronRight,
  Filter,
  RefreshCw,
  Check,
  Calendar,
  User,
  Tag,
  Target,
  Flag,
  FileText,
  AlertCircle,
  Clock,
  Layout
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'

interface FilterOption {
  id: number
  name?: string
  is_closed?: boolean
}

interface FilterSidebarProps {
  isOpen: boolean
  onClose: () => void
  filters: FilterState
  onFiltersChange: (filters: FilterState) => void
  trackers: FilterOption[]
  statuses: FilterOption[]
  priorities: FilterOption[]
  users: FilterOption[]
  versions: FilterOption[]
  categories?: FilterOption[]
  parentIssues?: FilterOption[]
  currentUser?: { id: number; name: string } | null
  visibleColumns?: number[]
  onVisibleColumnsChange?: (columns: number[]) => void
}

export interface FilterState {
  status: string[]
  tracker: string[]
  priority: string[]
  assignee: string[]
  version: string[]
  author: string[]
  category: string[]
  parent: string[]
}

interface FilterSection {
  key: keyof FilterState
  label: string
  icon: React.ReactNode
  options: FilterOption[]
}

export function IssueFilterSidebar({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  trackers,
  statuses,
  priorities,
  users,
  versions,
  categories = [],
  parentIssues = [],
  currentUser,
  visibleColumns,
  onVisibleColumnsChange
}: FilterSidebarProps) {
  const [mounted, setMounted] = useState(false)
  const t = useTranslations('issuesPage')
  const c = useTranslations('common')
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set([])
  )
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({})

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev)
      if (newSet.has(section)) {
        newSet.delete(section)
      } else {
        newSet.add(section)
      }
      return newSet
    })
  }

  const handleFilterToggle = (filterType: keyof FilterState, value: string) => {
    const currentValues = filters[filterType] || []
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value]

    onFiltersChange({
      ...filters,
      [filterType]: newValues
    })
  }

  const clearAllFilters = () => {
    onFiltersChange({
      status: [],
      tracker: [],
      priority: [],
      assignee: [],
      version: [],
      author: [],
      category: [],
      parent: []
    })
  }

  const getActiveFilterCount = () => {
    return Object.values(filters).flat().length
  }

  const filterSections: FilterSection[] = [
    {
      key: 'status',
      label: t('fields.status'),
      icon: <AlertCircle className="w-4 h-4" />,
      options: statuses
    },
    {
      key: 'tracker',
      label: t('fields.tracker'),
      icon: <Tag className="w-4 h-4" />,
      options: trackers
    },
    {
      key: 'priority',
      label: t('fields.priority'),
      icon: <Flag className="w-4 h-4" />,
      options: priorities
    },
    {
      key: 'assignee',
      label: t('fields.assignee'),
      icon: <User className="w-4 h-4" />,
      options: users
    },
    {
      key: 'author',
      label: t('by'),
      icon: <User className="w-4 h-4" />,
      options: users
    },
    {
      key: 'version',
      label: t('targetVersion'),
      icon: <Target className="w-4 h-4" />,
      options: versions
    },
    {
      key: 'category',
      label: t('category'),
      icon: <FileText className="w-4 h-4" />,
      options: categories
    },
    {
      key: 'parent',
      label: t('parentIssue'),
      icon: <Clock className="w-4 h-4" />,
      options: parentIssues
    }
  ]

  // Prepend "Me" option to user lists if current user is available
  const getUserOptionsWithMe = (options: FilterOption[], sectionKey: string) => {
    // Only add "Me" for assignee and author sections
    if ((sectionKey !== 'assignee' && sectionKey !== 'author') || !currentUser) {
      return options
    }

    // Check if current user is already in the list
    const currentUserExists = options.some(opt => opt.id === currentUser.id)

    // Create "Me" option
    const meOption: FilterOption = {
      id: currentUser.id,
      name: `Me (${currentUser.name})`
    }

    // If current user exists in list, filter them out and prepend "Me"
    if (currentUserExists) {
      const withoutCurrentUser = options.filter(opt => opt.id !== currentUser.id)
      return [meOption, ...withoutCurrentUser]
    }

    // Otherwise just prepend "Me"
    return [meOption, ...options]
  }

  const getFilteredOptions = (section: FilterSection) => {
    const searchTerm = searchTerms[section.key]?.toLowerCase() || ''
    const optionsWithMe = getUserOptionsWithMe(section.options, section.key)

    if (!searchTerm) return optionsWithMe
    return optionsWithMe.filter(opt =>
      opt.name?.toLowerCase().includes(searchTerm) ?? false
    )
  }

  // Helper to get closed status ids for the status section
  const getClosedStatusIds = (options: FilterOption[]) => {
    return options.filter(o => (o as any).is_closed).map(o => String(o.id))
  }

  const isClosedActiveForOptions = (options: FilterOption[]) => {
    const closedIds = getClosedStatusIds(options)
    if (closedIds.length === 0) return false
    return closedIds.every(id => (filters.status || []).includes(id))
  }

  const toggleClosedForOptions = (options: FilterOption[]) => {
    const closedIds = getClosedStatusIds(options)
    if (closedIds.length === 0) return

    const current = filters.status || []
    const allActive = closedIds.every(id => current.includes(id))

    let newStatus: string[]
    if (allActive) {
      // remove closed ids
      newStatus = current.filter(id => !closedIds.includes(id))
    } else {
      // add closed ids (dedupe)
      newStatus = Array.from(new Set([...current, ...closedIds]))
    }

    onFiltersChange({ ...filters, status: newStatus })
  }

  if (!mounted) return null

  return (
    <GlobalSidebar
      isOpen={isOpen}
      onClose={onClose}
      title={t('filtersTitle')}
      subtitle={getActiveFilterCount() > 0 ? t('filtersActive', { count: getActiveFilterCount() }) : t('noFiltersApplied')}
  icon={<Filter className="h-5 w-5" />}
      width="sm"
      footer={
        <div className="flex items-center justify-between w-full">
          <span className="text-xs text-muted-foreground">
            {getActiveFilterCount() === 0
              ? t('noFiltersApplied')
              : t('filtersActive', { count: getActiveFilterCount() })
            }
          </span>
          {getActiveFilterCount() > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllFilters}
              className="h-8"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              {t('clearAll')}
            </Button>
          )}
        </div>
      }
    >
      <ScrollArea className="h-full -mx-6 px-6">
          <div className="p-2">
            {filterSections.filter(section => section.options.length > 0).map((section) => {
              const isExpanded = expandedSections.has(section.key)
              const activeCount = filters[section.key]?.length || 0
              const filteredOptions = getFilteredOptions(section)
              const hasOptions = section.options.length > 0

              return (
                <div key={section.key} className="mb-1">
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(section.key)}
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2.5",
                      "hover:bg-muted/50 rounded-md transition-colors",
                      "text-sm font-medium group"
                    )}
                    disabled={!hasOptions}
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      )}
                      <div className="text-muted-foreground group-hover:text-foreground transition-colors">
                        {section.icon}
                      </div>
                      <span className={cn(
                        "text-foreground",
                        !hasOptions && "text-muted-foreground"
                      )}>
                        {section.label}
                      </span>

                      {/* Quick toggle for closed statuses */}
                      {section.key === 'status' && hasOptions && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleClosedForOptions(section.options) }}
                          className={cn(
                            'ml-2 text-xs px-2 py-0.5 rounded-md border',
                            isClosedActiveForOptions(section.options) ? 'bg-primary/10 border-primary text-primary' : 'bg-muted/10 border-transparent text-muted-foreground'
                          )}
                        >
                          {c('closed')}
                        </button>
                      )}
                    </div>
                    {activeCount > 0 && (
                      <Badge variant="default" className="h-5 px-1.5 text-xs">
                        {activeCount}
                      </Badge>
                    )}
                  </button>

                  {/* Section Content */}
                  {isExpanded && (
                    <div className="mt-1 mb-2 space-y-1 ml-4 mr-1">
                      {!hasOptions ? (
                        <div className="px-3 py-2 text-xs text-muted-foreground italic">
                          {t('noOptionsAvailable', { label: section.label.toLowerCase() })}
                        </div>
                      ) : (
                        <>
                          {/* Search for sections with many options */}
                          {section.options.length > 8 && (
                            <div className="px-2 pb-2">
                              <Input
                                  placeholder={`${c('search')} ${section.label.toLowerCase()}...`}
                                  value={searchTerms[section.key] || ''}
                                  onChange={(e) => setSearchTerms(prev => ({
                                    ...prev,
                                    [section.key]: e.target.value
                                  }))}
                                  className="h-8 text-xs"
                                />
                            </div>
                          )}

                          {/* Options List */}
                          {filteredOptions.length === 0 ? (
                            <div className="px-3 py-2 text-xs text-muted-foreground italic">
                              {t('noMatchesFound')}
                            </div>
                          ) : (
                            filteredOptions.map((option) => {
                              const isSelected = filters[section.key]?.includes(String(option.id))
                              return (
                                <label
                                  key={option.id}
                                  className={cn(
                                    "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer",
                                    "hover:bg-muted/50 transition-colors group",
                                    isSelected && "bg-primary/5 hover:bg-primary/10"
                                  )}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleFilterToggle(section.key, String(option.id))}
                                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                                  />
                                  <span className={cn(
                                    "text-sm flex-1",
                                    isSelected ? "text-foreground font-medium" : "text-muted-foreground group-hover:text-foreground"
                                  )}>
                                    {option.name || `ID: ${option.id}`}
                                  </span>
                                  {isSelected && (
                                    <Check className="w-4 h-4 text-primary" />
                                  )}
                                </label>
                              )
                            })
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )
            })}

            {/* Column Visibility Section at bottom (only show if props are provided) */}
            {visibleColumns !== undefined && onVisibleColumnsChange && statuses.length > 0 && (
              <div className="mb-1">
                <button
                  onClick={() => toggleSection('columnVisibility')}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2.5",
                    "hover:bg-muted/50 rounded-md transition-colors",
                    "text-sm font-medium group"
                  )}
                >
                  <div className="flex items-center gap-2">
                    {expandedSections.has('columnVisibility') ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                    <div className="text-muted-foreground group-hover:text-foreground transition-colors">
                      <Layout className="w-4 h-4" />
                    </div>
                    <span className="text-foreground">Column Visibility</span>
                  </div>
                  {visibleColumns.length < statuses.length && (
                    <Badge variant="default" className="h-5 px-1.5 text-xs">
                      {statuses.length - visibleColumns.length} hidden
                    </Badge>
                  )}
                </button>
                {expandedSections.has('columnVisibility') && (
                  <div className="mt-1 mb-2 space-y-1 ml-4 mr-1">
                    {statuses.map((status) => {
                      const isVisible = visibleColumns.includes(status.id)
                      return (
                        <label
                          key={status.id}
                          className={cn(
                            "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer",
                            "hover:bg-muted/50 transition-colors group",
                            isVisible && "bg-primary/5 hover:bg-primary/10"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={isVisible}
                            onChange={() => {
                              if (isVisible) {
                                onVisibleColumnsChange(visibleColumns.filter(id => id !== status.id))
                              } else {
                                onVisibleColumnsChange([...visibleColumns, status.id])
                              }
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                          />
                          <span className={cn(
                            "text-sm flex-1",
                            isVisible ? "text-foreground font-medium" : "text-muted-foreground group-hover:text-foreground"
                          )}>
                            {status.name || `ID: ${status.id}`}
                          </span>
                          {isVisible && (
                            <Check className="w-4 h-4 text-primary" />
                          )}
                        </label>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollArea>
    </GlobalSidebar>
  )
}
